/**
 * /emissoes/nfse e /emissoes/nfe — G7 §13.
 *
 * TRÊS CONTADORES QUE NÃO SÃO A MESMA COISA
 *
 *   A tabela de NFS-e mostra `RPS`, `nº` e `nDPS` em colunas separadas, e essa
 *   separação é o assunto de meio gate anterior:
 *
 *     RPS    numeração NOSSA, alocada antes de transmitir
 *     nº     o número que a PREFEITURA deu à NFS-e
 *     nDPS   o identificador do documento no Padrão Nacional
 *
 *   O G6B.3 os confundiu três vezes, e cada confusão custou uma tentativa de
 *   emissão. Uma coluna "número" que juntasse os três seria a mesma confusão
 *   servida de volta a quem opera.
 *
 * A COLUNA `origem` DIZ SE O DOCUMENTO É NOSSO
 *
 *   `retro_custody` marca notas que a plataforma não emitiu — foram emitidas
 *   pelo canal antes do cutover e custodiadas depois. Elas contam como
 *   documento e não contam como emissão nossa, e a tela precisa dizer qual é
 *   qual sem que ninguém tenha que ir ao banco perguntar.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useSession } from '../lib/session.js';
import { fetchNfeDocuments, fetchNfseDocuments } from '../lib/queries.js';
import { AsyncPanel, ausente, dataBr } from '../components/States.js';
import { DocumentStatusBadge } from '../components/DocumentStatusBadge.js';
import { EnvironmentCell } from '../components/EnvironmentCell.js';

export function EmissoesNfsePage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';
  const query = useQuery({
    queryKey: ['nfse', id],
    queryFn: () => fetchNfseDocuments(id),
    enabled: id !== '',
  });

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">NFS-e</h1>
        <p className="of-page__subtitle">Notas fiscais de serviço desta identidade fiscal.</p>
      </header>

      <AsyncPanel
        query={query}
        title="NFS-e"
        emptyTitle="Nenhuma NFS-e registrada"
        emptyDetail="Esta identidade fiscal não tem documentos de serviço nesta plataforma."
      >
        {(rows) => (
          <div className="of-table-wrap">
            <table className="of-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Amb.</th>
                  <th>Provedor</th>
                  <th>Série</th>
                  <th>RPS</th>
                  <th>nº NFS-e</th>
                  <th>nDPS</th>
                  <th>Município</th>
                  <th>Origem</th>
                  <th>Criado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <DocumentStatusBadge status={d.status} />
                    </td>
                    <td>
                      <EnvironmentCell value={d.environment} />
                    </td>
                    <td className="of-mono">{ausente(d.provider_id)}</td>
                    <td className="of-mono">{ausente(d.serie)}</td>
                    <td className="of-mono">{ausente(d.provider_rps_number)}</td>
                    <td className="of-mono">
                      {ausente(d.provider_document_ref ?? d.nfse_number)}
                    </td>
                    <td className="of-mono">{ausente(d.numero)}</td>
                    <td className="of-mono">{ausente(d.issuer_municipality_code)}</td>
                    <td>
                      {d.retro_custody === true ? (
                        <span className="of-tag of-tag--muted" title="Emitida fora da plataforma e custodiada depois">
                          retrocustódia
                        </span>
                      ) : (
                        <span className="of-tag">plataforma</span>
                      )}
                    </td>
                    <td>{dataBr(d.created_at)}</td>
                    <td>
                      <Link to="/documentos/$id" params={{ id: d.id }} className="of-link">
                        detalhe
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>
    </section>
  );
}

export function EmissoesNfePage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';
  const query = useQuery({
    queryKey: ['nfe', id],
    queryFn: () => fetchNfeDocuments(id),
    enabled: id !== '',
  });

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">NF-e</h1>
        <p className="of-page__subtitle">Notas fiscais eletrônicas desta identidade fiscal.</p>
      </header>

      <AsyncPanel
        query={query}
        title="NF-e"
        emptyTitle="Nenhuma NF-e registrada"
        emptyDetail="Esta identidade fiscal não tem documentos de mercadoria nesta plataforma."
      >
        {(rows) => (
          <div className="of-table-wrap">
            <table className="of-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Amb.</th>
                  <th>Modelo</th>
                  <th>Série</th>
                  <th>Número</th>
                  <th>Chave de acesso</th>
                  <th>cStat</th>
                  <th>Protocolo</th>
                  <th>Autorizada</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <DocumentStatusBadge status={d.status} />
                    </td>
                    <td>
                      <EnvironmentCell value={d.environment} />
                    </td>
                    <td className="of-mono">{ausente(d.model)}</td>
                    <td className="of-mono">{ausente(d.serie)}</td>
                    <td className="of-mono">{ausente(d.number)}</td>
                    {/* A chave tem 44 dígitos e é o identificador que a SEFAZ usa.
                        Ela aparece inteira: truncar um identificador fiscal para
                        caber na coluna é o tipo de gentileza que atrapalha. */}
                    <td className="of-mono of-cell--key">{ausente(d.access_key)}</td>
                    <td className="of-mono">
                      {ausente(d.cstat)}
                      {d.xmotivo !== null && <span className="of-cell__hint"> {d.xmotivo}</span>}
                    </td>
                    <td className="of-mono">{ausente(d.protocol_number)}</td>
                    <td>{dataBr(d.authorized_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>
    </section>
  );
}
